import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { filterMfeNames, formatProductName, hasMfeOverlap, matchesProductLabel } from "../product-utils";
import { loadSnapshots, filterIgnoredMfes } from "../snapshot-utils";
import { getComparisonMfeNames, isEnvironmentMissing, isEnvironmentOutdated } from "../components/environment-overview.logic";
import type { AuditEntry, EnvironmentsResponse, PullRequestInfo, ProductsResponse, Snapshot } from "../types";

import "@bee/pattern-library";

type AttentionTone = "warning" | "info" | "success";

interface AttentionItem {
  title: string;
  metric: string;
  detail: string;
  actionLabel: string;
  route: string;
  tone: AttentionTone;
}

interface EnvironmentHealth {
  deployedCount: number;
  trackedCount: number;
  alignedCount: number;
  outdatedNames: string[];
  missingNames: string[];
  coverageRatio: number;
}

interface ActivityItem {
  kind: "Promotion" | "Pull Request";
  title: string;
  detail: string;
  timestamp: string;
}

@customElement("dashboard-page")
export class DashboardPage extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
      color: var(--colors-text-brand-900, #002266);
    }

    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: var(--spacing-16, 16px);
    }

    @media (max-width: 900px) {
      .grid { grid-template-columns: 1fr; }
    }

    .span-2 {
      grid-column: 1 / -1;
    }

    .section-title {
      font-size: 14px;
      font-weight: 600;
      color: var(--colors-text-brand-900, #002266);
      margin: 0 0 var(--spacing-12, 12px);
    }

    .section-copy {
      margin: 0 0 14px;
      font-size: 13px;
      line-height: 1.5;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .env-cards {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: var(--spacing-12, 12px);
      grid-column: 1 / -1;
    }

    @media (max-width: 900px) {
      .env-cards { grid-template-columns: 1fr; }
    }

    bee-card {
      --padding: var(--spacing-16, 16px);
    }

    bee-card::part(body) {
      padding: var(--spacing-16, 16px);
    }

    bee-card::part(base) {
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
      box-shadow: none;
    }

    .attention-list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 12px;
    }

    .attention-item {
      display: flex;
      flex-direction: column;
      gap: 10px;
      min-height: 170px;
      padding: 16px;
      border-radius: 12px;
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
      background: var(--colors-background-bg-primary, #fff);
    }

    .attention-item.warning {
      background: var(--colors-background-bg-warning-50, #fffcf0);
      border-color: var(--colors-background-bg-warning-50, #fffcf0);
    }

    .attention-item.info {
      background: var(--colors-background-bg-secondary, #f9fafb);
      border-color: var(--colors-border-border-secondary, #eaecf0);
    }

    .attention-item.success {
      background: var(--colors-background-bg-success-50, #f0fdf4);
      border-color: var(--colors-background-bg-success-50, #f0fdf4);
    }

    .attention-metric {
      font-size: 28px;
      font-weight: 700;
      letter-spacing: -0.03em;
    }

    .attention-title {
      font-size: 14px;
      font-weight: 600;
      margin: 0;
    }

    .attention-detail {
      font-size: 13px;
      line-height: 1.5;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin: 0;
      flex: 1;
    }

    .env-card-head {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 12px;
      margin-bottom: 12px;
    }

    .env-card-title {
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .env-card-chip {
      padding: 4px 8px;
      border-radius: 999px;
      font-size: 12px;
      font-weight: 600;
      background: var(--colors-background-bg-gray-50, #f5f6f8);
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .env-card-chip.warning {
      background: var(--colors-background-bg-warning-50, #fffcf0);
      color: var(--colors-foreground-fg-warning-700, #b54708);
    }

    .env-card-chip.success {
      background: var(--colors-background-bg-success-50, #f0fdf4);
      color: var(--colors-foreground-fg-success-700, #15803d);
    }

    .readiness-meter {
      height: 10px;
      width: 100%;
      overflow: hidden;
      border-radius: 999px;
      background: var(--colors-background-bg-gray-50, #f5f6f8);
      margin-bottom: 12px;
    }

    .readiness-fill {
      height: 100%;
      border-radius: inherit;
      background: var(--colors-text-brand-900, #002266);
    }

    .env-card-stat {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      padding: 2px 0;
    }

    .stat-value {
      font-weight: 500;
    }

    .env-card-footer {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--colors-border-border-secondary, #eaecf0);
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      line-height: 1.5;
    }

    .outdated { color: var(--colors-status-warning-600, #dc6803); }
    .missing { color: var(--colors-status-error-600, #d92d20); }

    .gap-columns {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    @media (max-width: 900px) {
      .gap-columns {
        grid-template-columns: 1fr;
      }
    }

    .gap-panel {
      padding: 14px;
      border-radius: 12px;
      background: var(--colors-background-bg-secondary, #f9fafb);
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
    }

    .gap-title {
      margin: 0 0 10px;
      font-size: 13px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .gap-group + .gap-group {
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--colors-border-border-secondary, #eaecf0);
    }

    .gap-label {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: 12px;
      margin: 0 0 8px;
      font-size: 13px;
      font-weight: 600;
    }

    .gap-value {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      font-weight: 500;
    }

    .tag-row {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .mfe-tag {
      display: inline-flex;
      align-items: center;
      padding: 5px 9px;
      border-radius: 999px;
      background: var(--colors-background-bg-gray-50, #f5f6f8);
      color: var(--colors-text-brand-900, #002266);
      font-size: 12px;
      font-weight: 500;
    }

    .activity-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .activity-item {
      display: flex;
      flex-direction: column;
      gap: 6px;
      padding: 14px;
      border-radius: 12px;
      background: var(--colors-background-bg-secondary, #f9fafb);
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
    }

    .activity-item-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      font-size: 12px;
    }

    .activity-kind {
      display: inline-flex;
      align-items: center;
      padding: 4px 8px;
      border-radius: 999px;
      background: var(--colors-background-bg-gray-50, #f5f6f8);
      color: var(--colors-foreground-fg-brand-600, #4a90d9);
      font-weight: 600;
    }

    .activity-time {
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .activity-title {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
    }

    .activity-detail {
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .pr-list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-8, 8px);
    }

    .pr-link {
      display: block;
      font-size: 13px;
      color: var(--colors-text-brand-900, #002266);
      text-decoration: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .pr-link:hover { text-decoration: underline; }

    .pr-meta {
      font-size: 12px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .shortcut-row {
      display: flex;
      gap: var(--spacing-8, 8px);
      flex-wrap: wrap;
    }

    .shortcut-copy {
      font-size: 13px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin: 0 0 var(--spacing-12, 12px);
      line-height: 1.5;
    }

    .snapshot-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
      gap: 12px;
      margin-bottom: 12px;
    }

    .snapshot-stat {
      padding: 14px;
      border-radius: 12px;
      background: var(--colors-background-bg-secondary, #f9fafb);
      border: 1px solid var(--colors-border-border-secondary, #eaecf0);
    }

    .snapshot-stat-label {
      display: block;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: 8px;
    }

    .snapshot-stat-value {
      display: block;
      font-size: 18px;
      font-weight: 600;
      line-height: 1.3;
      color: var(--colors-text-brand-900, #002266);
    }

    .empty {
      font-size: 13px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
    }

    .loading {
      display: flex;
      justify-content: center;
      padding: var(--spacing-24, 24px);
      grid-column: 1 / -1;
    }
  `;

  @property({ attribute: false }) activeProductFilter: string | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;

  @state() private _environments: EnvironmentsResponse | null = null;
  @state() private _prs: PullRequestInfo[] = [];
  @state() private _auditEntries: AuditEntry[] = [];
  @state() private _loading = true;

  private _refreshInterval: ReturnType<typeof setInterval> | null = null;
  private readonly REFRESH_INTERVAL_MS = 300000; // 5 minutes

  async connectedCallback() {
    super.connectedCallback();
    await this._loadData();
    this._setupAutoRefresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._clearAutoRefresh();
  }

  private _setupAutoRefresh() {
    // Only start refresh if page is currently visible
    if (!document.hidden) {
      this._startRefreshInterval();
    }

    // Listen for visibility changes
    document.addEventListener("visibilitychange", this._handleVisibilityChange);
  }

  private _clearAutoRefresh() {
    document.removeEventListener("visibilitychange", this._handleVisibilityChange);
    this._stopRefreshInterval();
  }

  private _handleVisibilityChange = () => {
    if (document.hidden) {
      this._stopRefreshInterval();
    } else {
      this._startRefreshInterval();
    }
  };

  private _startRefreshInterval() {
    if (this._refreshInterval) return; // Already running
    this._refreshInterval = setInterval(() => this._loadData(false), this.REFRESH_INTERVAL_MS);
  }

  private _stopRefreshInterval() {
    if (this._refreshInterval) {
      clearInterval(this._refreshInterval);
      this._refreshInterval = null;
    }
  }

  private async _loadData(showLoading = true) {
    if (showLoading) {
      this._loading = true;
    }
    try {
      const [envRes, prRes, auditRes] = await Promise.allSettled([
        api.getEnvironments(),
        api.getPullRequests(),
        api.getAuditLog(1, 8),
      ]);

      if (envRes.status === "fulfilled") {
        this._environments = envRes.value;
      }

      if (prRes.status === "fulfilled") {
        this._prs = prRes.value.pullRequests;
      }

      if (auditRes.status === "fulfilled") {
        this._auditEntries = auditRes.value.entries;
      }
    } catch {
      // Panels handle their own error states; overview degrades gracefully
    } finally {
      if (showLoading) {
        this._loading = false;
      }
    }
  }

  private _navigate(path: string) {
    this.dispatchEvent(new CustomEvent("navigate", { detail: path, bubbles: true, composed: true }));
  }

  private _getFilteredPullRequests() {
    return this._prs.filter((pr) =>
      matchesProductLabel(pr.labels, this.activeProductFilter),
    );
  }

  private _getFilteredAuditEntries() {
    return this._auditEntries.filter((entry) =>
      matchesProductLabel(entry.labels, this.activeProductFilter),
    );
  }

  private _getFilteredSnapshots() {
    return loadSnapshots()
      .filter((snapshot) =>
        this.activeProductFilter
          ? snapshot.product
            ? snapshot.product === this.activeProductFilter
            : hasMfeOverlap(Object.keys(snapshot.versions), this.activeProductFilter, this.products)
          : true,
      )
      .sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime());
  }

  private _getEnvironmentHealth(env: "dev" | "stage" | "prod"): EnvironmentHealth {
    if (!this._environments) {
      return {
        deployedCount: 0,
        trackedCount: 0,
        alignedCount: 0,
        outdatedNames: [],
        missingNames: [],
        coverageRatio: 0,
      };
    }

    const deployedNames = filterMfeNames(
      Object.keys(filterIgnoredMfes(this._environments[env].mfes)).sort(),
      this.activeProductFilter,
      this.products,
    );

    if (env === "dev") {
      return {
        deployedCount: deployedNames.length,
        trackedCount: deployedNames.length,
        alignedCount: deployedNames.length,
        outdatedNames: [],
        missingNames: [],
        coverageRatio: deployedNames.length > 0 ? 1 : 0,
      };
    }

    const trackedNames = filterMfeNames(
      getComparisonMfeNames(this._environments, env),
      this.activeProductFilter,
      this.products,
    );
    const outdatedNames = trackedNames.filter((name) =>
      !isEnvironmentMissing(this._environments!, name, env) && isEnvironmentOutdated(this._environments!, name, env),
    );
    const missingNames = trackedNames.filter((name) => isEnvironmentMissing(this._environments!, name, env));
    const alignedCount = Math.max(trackedNames.length - outdatedNames.length - missingNames.length, 0);

    return {
      deployedCount: deployedNames.length,
      trackedCount: trackedNames.length,
      alignedCount,
      outdatedNames,
      missingNames,
      coverageRatio: trackedNames.length > 0 ? alignedCount / trackedNames.length : 0,
    };
  }

  private _buildAttentionItems(
    stageHealth: EnvironmentHealth,
    prodHealth: EnvironmentHealth,
    pullRequests: PullRequestInfo[],
    snapshots: Snapshot[],
  ): AttentionItem[] {
    const items: AttentionItem[] = [];
    const stageDriftCount = stageHealth.outdatedNames.length + stageHealth.missingNames.length;
    const prodDriftCount = prodHealth.outdatedNames.length + prodHealth.missingNames.length;
    const agedPullRequests = pullRequests.filter((pr) => Date.now() - new Date(pr.createdAt).getTime() >= 1000 * 60 * 60 * 24 * 2);
    const latestSnapshot = snapshots[0];

    if (stageDriftCount > 0) {
      items.push({
        title: "Stage drift",
        metric: this._formatCount(stageDriftCount, "MFE"),
        detail: `${stageHealth.alignedCount}/${stageHealth.trackedCount} aligned with dev. Review the remaining gaps before promotion work starts.`,
        actionLabel: "Review versions",
        route: "/versions",
        tone: "warning",
      });
    }

    if (prodDriftCount > 0) {
      items.push({
        title: "Prod drift",
        metric: this._formatCount(prodDriftCount, "MFE"),
        detail: `${prodHealth.alignedCount}/${prodHealth.trackedCount} aligned with stage. Confirm what is still waiting on prod rollout.`,
        actionLabel: "Open versions",
        route: "/versions",
        tone: "warning",
      });
    }

    if (pullRequests.length > 0) {
      items.push({
        title: "Release & promotion PRs in flight",
        metric: this._formatCount(pullRequests.length, "PR"),
        detail: agedPullRequests.length > 0
          ? `${this._formatCount(agedPullRequests.length, "PR")} older than two days. Clear review bottlenecks before more drift accumulates.`
          : "Active release or promotion work already exists. Check whether those PRs cover the current drift before creating new ones.",
        actionLabel: "Open PRs",
        route: "/promotions",
        tone: agedPullRequests.length > 0 ? "warning" : "info",
      });
    }

    if (!latestSnapshot) {
      items.push({
        title: "No saved snapshot",
        metric: "0 snapshots",
        detail: "There is no saved rollback or comparison point for the active product yet.",
        actionLabel: "Create snapshot",
        route: "/snapshots",
        tone: "info",
      });
    } else if (Date.now() - new Date(latestSnapshot.timestamp).getTime() >= 1000 * 60 * 60 * 24 * 7) {
      items.push({
        title: "Snapshot may be stale",
        metric: this._formatRelativeTime(latestSnapshot.timestamp),
        detail: `${latestSnapshot.name} is the latest saved state. Capture a newer snapshot before a promotion window if you need a clean rollback point.`,
        actionLabel: "Manage snapshots",
        route: "/snapshots",
        tone: "info",
      });
    }

    if (items.length === 0) {
      items.push({
        title: "No immediate blockers",
        metric: "All clear",
        detail: "Stage and prod are aligned, PR flow is under control, and your latest snapshot is still fresh.",
        actionLabel: "Inspect versions",
        route: "/versions",
        tone: "success",
      });
    }

    return items;
  }

  private _buildRecentActivity(pullRequests: PullRequestInfo[], auditEntries: AuditEntry[]): ActivityItem[] {
    return [
      ...auditEntries.map((entry) => ({
        kind: "Promotion" as const,
        title: `${entry.source.toUpperCase()} -> ${entry.target.toUpperCase()} promotion`,
        detail: `PR #${entry.prNumber} by ${entry.author} moved ${this._formatCount(entry.changes.length, "MFE")}.`,
        timestamp: entry.closedAt,
      })),
      ...pullRequests.map((pr) => ({
        kind: "Pull Request" as const,
        title: pr.title,
        detail: `PR #${pr.number} by ${pr.author} proposes ${this._formatCount(pr.changes.length, "change")}.`,
        timestamp: pr.createdAt,
      })),
    ]
      .sort((left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime())
      .slice(0, 3);
  }

  private _formatCount(count: number, noun: string) {
    return `${count} ${noun}${count === 1 ? "" : "s"}`;
  }

  private _formatRelativeTime(dateString: string) {
    const delta = Date.now() - new Date(dateString).getTime();
    const hours = Math.floor(delta / (1000 * 60 * 60));

    if (hours < 1) return "just now";
    if (hours < 24) return `${hours}h ago`;

    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;

    const weeks = Math.floor(days / 7);
    return `${weeks}w ago`;
  }

  private _formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  private _renderTags(names: string[]) {
    if (names.length === 0) {
      return html`<span class="empty">None</span>`;
    }

    return html`
      <div class="tag-row">
        ${names.slice(0, 5).map((name) => html`<span class="mfe-tag">${name}</span>`)}
        ${names.length > 5 ? html`<span class="mfe-tag">+${names.length - 5} more</span>` : nothing}
      </div>
    `;
  }

  render() {
    if (this._loading) {
      return html`<div class="loading"><bee-spinner size="large" type="blue"></bee-spinner></div>`;
    }

    const snapshots = this._getFilteredSnapshots();
    const filteredSnapshotCount = snapshots.length;
    const filteredPullRequests = this._getFilteredPullRequests();
    const filteredAuditEntries = this._getFilteredAuditEntries();
    const stageHealth = this._getEnvironmentHealth("stage");
    const prodHealth = this._getEnvironmentHealth("prod");
    const attentionItems = this._buildAttentionItems(stageHealth, prodHealth, filteredPullRequests, snapshots);
    const recentActivity = this._buildRecentActivity(filteredPullRequests, filteredAuditEntries);
    const latestSnapshot = snapshots[0] ?? null;

    return html`
      <div class="grid">
        <bee-card class="span-2">
          <h3 class="section-title">Attention Required</h3>
          <p class="section-copy">These are the fastest paths to reduce delivery risk for the active product.</p>
          <div class="attention-list">
            ${attentionItems.map((item) => html`
              <div class="attention-item ${item.tone}">
                <span class="attention-metric">${item.metric}</span>
                <h4 class="attention-title">${item.title}</h4>
                <p class="attention-detail">${item.detail}</p>
                <bee-button
                  label=${item.actionLabel}
                  bee-type=${item.tone === "warning" ? "secondary-color" : "secondary-grey"}
                  size="small"
                  @click=${() => this._navigate(item.route)}
                ></bee-button>
              </div>
            `)}
          </div>
        </bee-card>

        <div class="env-cards">
          ${(["dev", "stage", "prod"] as const).map((env) => {
            const health = this._getEnvironmentHealth(env);
            const hasGaps = health.outdatedNames.length + health.missingNames.length > 0;
            const chipClass = env === "dev" ? "success" : hasGaps ? "warning" : "success";
            const chipLabel = env === "dev"
              ? "Source of truth"
              : hasGaps
                ? `${this._formatCount(health.outdatedNames.length + health.missingNames.length, "gap")}`
                : "Aligned";

            return html`
              <bee-card>
                <div class="env-card-head">
                  <div class="env-card-title">${env}</div>
                  <span class="env-card-chip ${chipClass}">${chipLabel}</span>
                </div>
                ${env !== "dev" ? html`
                  <div class="readiness-meter">
                    <div class="readiness-fill" style=${`width:${Math.round(health.coverageRatio * 100)}%`}></div>
                  </div>
                ` : nothing}
                <div class="env-card-stat">
                  <span>MFEs deployed</span>
                  <span class="stat-value">${health.deployedCount}</span>
                </div>
                ${env !== "dev" ? html`
                  <div class="env-card-stat">
                    <span>Aligned with ${env === "stage" ? "dev" : "stage"}</span>
                    <span class="stat-value">${health.alignedCount}/${health.trackedCount}</span>
                  </div>
                  <div class="env-card-stat">
                    <span>Outdated</span>
                    <span class="stat-value ${health.outdatedNames.length > 0 ? "outdated" : ""}">${health.outdatedNames.length}</span>
                  </div>
                  <div class="env-card-stat">
                    <span>Missing</span>
                    <span class="stat-value ${health.missingNames.length > 0 ? "missing" : ""}">${health.missingNames.length}</span>
                  </div>
                ` : html`
                  <div class="env-card-stat">
                    <span>Ready to compare</span>
                    <span class="stat-value">${health.trackedCount}</span>
                  </div>
                `}
                <div class="env-card-footer">
                  ${env === "dev"
                    ? html`Use dev as the baseline when deciding what should move next.`
                    : hasGaps
                      ? this._renderTags([...health.outdatedNames, ...health.missingNames])
                      : html`No version drift detected for this environment.`}
                </div>
              </bee-card>
            `;
          })}
        </div>

        <bee-card>
          <h3 class="section-title">Product Rollout Gaps</h3>
          <p class="section-copy">See which MFEs are holding this product back before opening the full comparison page.</p>
          <div class="gap-columns">
            <div class="gap-panel">
              <h4 class="gap-title">Stage Readiness</h4>
              <div class="gap-group">
                <div class="gap-label">
                  <span>Outdated</span>
                  <span class="gap-value">${stageHealth.outdatedNames.length}</span>
                </div>
                ${this._renderTags(stageHealth.outdatedNames)}
              </div>
              <div class="gap-group">
                <div class="gap-label">
                  <span>Missing</span>
                  <span class="gap-value">${stageHealth.missingNames.length}</span>
                </div>
                ${this._renderTags(stageHealth.missingNames)}
              </div>
            </div>
            <div class="gap-panel">
              <h4 class="gap-title">Prod Readiness</h4>
              <div class="gap-group">
                <div class="gap-label">
                  <span>Outdated</span>
                  <span class="gap-value">${prodHealth.outdatedNames.length}</span>
                </div>
                ${this._renderTags(prodHealth.outdatedNames)}
              </div>
              <div class="gap-group">
                <div class="gap-label">
                  <span>Missing</span>
                  <span class="gap-value">${prodHealth.missingNames.length}</span>
                </div>
                ${this._renderTags(prodHealth.missingNames)}
              </div>
            </div>
          </div>
          <div style="margin-top: 12px">
            <bee-button label="Open versions" bee-type="secondary-grey" size="small" @click=${() => this._navigate("/versions")}></bee-button>
          </div>
        </bee-card>

        <bee-card>
          <h3 class="section-title">Recent Activity</h3>
          <p class="section-copy">Promotion history and active pull requests are combined here so you can spot movement without switching views.</p>
          ${recentActivity.length === 0
            ? html`<span class="empty">No recent activity for this product yet</span>`
            : html`
              <div class="activity-list">
                ${recentActivity.map((item) => html`
                  <div class="activity-item">
                    <div class="activity-item-top">
                      <span class="activity-kind">${item.kind}</span>
                      <span class="activity-time">${this._formatRelativeTime(item.timestamp)}</span>
                    </div>
                    <h4 class="activity-title">${item.title}</h4>
                    <p class="activity-detail">${item.detail}</p>
                  </div>
                `)}
              </div>
            `}
          <div class="shortcut-row" style="margin-top: 12px">
            <bee-button label="Releases & Promotions" bee-type="secondary-grey" size="small" @click=${() => this._navigate("/promotions")}></bee-button>
            <bee-button label="View Audit Log" bee-type="secondary-grey" size="small" @click=${() => this._navigate("/audit")}></bee-button>
          </div>
        </bee-card>

        <bee-card class="span-2">
          <h3 class="section-title">Snapshot Insights</h3>
          <p class="shortcut-copy">Snapshots remain product-aware, so these details reflect the filtered product and whether a recent rollback point exists.</p>
          <div class="snapshot-grid">
            <div class="snapshot-stat">
              <span class="snapshot-stat-label">Saved snapshots</span>
              <span class="snapshot-stat-value">${filteredSnapshotCount}</span>
            </div>
            <div class="snapshot-stat">
              <span class="snapshot-stat-label">Latest snapshot</span>
              <span class="snapshot-stat-value">${latestSnapshot ? latestSnapshot.name : "None saved"}</span>
            </div>
            <div class="snapshot-stat">
              <span class="snapshot-stat-label">Captured from</span>
              <span class="snapshot-stat-value">${latestSnapshot ? latestSnapshot.environment.toUpperCase() : "-"}</span>
            </div>
            <div class="snapshot-stat">
              <span class="snapshot-stat-label">Freshness</span>
              <span class="snapshot-stat-value">${latestSnapshot ? this._formatRelativeTime(latestSnapshot.timestamp) : "No history yet"}</span>
            </div>
          </div>
          ${latestSnapshot
            ? html`<p class="section-copy">Latest snapshot captured ${this._formatDate(latestSnapshot.timestamp)} and can be reused for comparison, preview, or rollback planning.</p>`
            : html`<span class="empty">Create a snapshot before a release window if you want a clean rollback reference.</span>`}
          <div class="shortcut-row">
            <bee-button label="Manage Snapshots" bee-type="secondary-grey" size="small" @click=${() => this._navigate("/snapshots")}></bee-button>
            <bee-button label="Open Preview Builder" bee-type="secondary-grey" size="small" @click=${() => this._navigate("/preview")}></bee-button>
          </div>
        </bee-card>
      </div>
    `;
  }
}
